import React, { useEffect, useRef, useState } from 'react';
import { styleDefinitions as styles } from '../../theme/styles';
import ReactDOM from 'react-dom';
import { MoreVertical, Plus, Edit3, FolderPlus } from 'lucide-react';

interface CategoryMenuProps {
  categoryId: string;
  categoryName: string;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onAddItem: () => void;
  onRename: (newName: string) => void;
  onAddSubcategory: (name: string) => void;
}

export const CategoryMenu: React.FC<CategoryMenuProps> = ({
  categoryId,
  categoryName,
  isOpen,
  onToggle,
  onClose,
  onAddItem,
  onRename,
  onAddSubcategory}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      // Calculate menu position when opened
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setMenuPosition({
          top: rect.bottom + 4,
          left: rect.right - 192 // 192px is the width of the menu (w-48)
        });
      }
      
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [isOpen, onClose]);

  const handleRename = () => {
    const newName = prompt(`Change name for "${categoryName}"`, categoryName);
    if (newName && newName !== categoryName) {
      onRename(newName);
    }
    onClose();
  };

  const handleAddSubcategory = () => {
    // Trigger the modal through the parent component
    onAddSubcategory('');
    onClose();
  };


  return (
    <div style={styles.table.actionMenuWrapper}>
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        style={styles.table.actionButton}
        title="Category Actions"
        type="button"
      >
        <MoreVertical size={16} style={styles.table.actionIcon} />
      </button>
      
      {isOpen && ReactDOM.createPortal(
        <div
          ref={menuRef}
          style={{
            ...styles.table.menuContainer,
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAddItem();
              onClose();
            }}
            style={styles.table.menuButton}
          >
            <Plus size={16} style={styles.table.menuButtonIcon} />
            Add item
          </button>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleRename();
            }}
            style={styles.table.menuButton}
          >
            <Edit3 size={16} style={styles.table.menuButtonIcon} />
            Change name
          </button>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleAddSubcategory();
            }}
            style={styles.table.menuButton}
          >
            <FolderPlus size={16} style={styles.table.menuButtonIcon} />
            Add secondary category
          </button>
          
        </div>,
        document.body
      )}
    </div>
  );
};